import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Button, ButtonBase, Collapse, Hidden } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import FilterListIcon from '@material-ui/icons/FilterList'
import RefreshIcon from '@material-ui/icons/Refresh'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
// import TimePeriodForm from '../../components/TimePeriodForm'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { INVENTORY_ALLOCATE_LIST } from '../../graphql/inventoryAllocateAvailability'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { addDaysToDate, DateObject, getTodayDate } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { Accommodation } from '../../types/accommodation'
import { Channel } from '../../types/channel'
import {
  AllocatedData,
  AllocatedListInput
} from '../../types/inventoryAllocatedList'
import { RoomTypeBrief } from '../../types/roomtype'
import AllocateFilterBox from './AllocateFilterBox'
import AllocateMobile from './AllocateMobile'
import AllocateRoomtypeBox from './AllocateRoomTypeBox'
import InventoryDateRow from './InventoryDateRow'
import InventorySkeleton from './InventorySkeleton.skeleton'
import useStyles from './styles/index.styles'

function InventoryAllocateAvailability() {
  const classes = useStyles()
  const [input, setinput] = useState<AllocatedListInput>({
    from: '',
    propertyCode: '',
    to: '',
    channelID: undefined,
    roomTypeCode: undefined
  })
  const [autocompleteLabelAccommodation, setautocompleteLabelAccommodation] =
    useState<any>()
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [autoCompleteLabelRoomType, setautoCompleteLabelRoomType] =
    useState<any>({})
  const [autoCompleteLabelChannel, setautoCompleteLabelChannel] = useState<any>(
    {}
  )
  const [dateObject, setDateObject] = useState<DateObject>({
    gregorianStartDate: getTodayDate(),
    gregorianEndDate: addDaysToDate(getTodayDate(), 13)
  })

  const { data: accommodationsData, loading: loadingForAccommodationData } =
    useQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })

  const [
    getAllocatedList,
    { data: allocatedListData, loading: loadingForAllocateList, refetch }
  ] = useLazyQuery<{
    allocatedList: AllocatedData
  }>(INVENTORY_ALLOCATE_LIST, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [getRoomTypes, { data: roomTypeData, loading: loadingForRoomType }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY)

  const { data: channelsData, loading: loadingForChannel } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY)

  const handleClickOnFilterButton = () => {
    setShowFilters(!showFilters)
  }
  const selectedDayInDatePickerWeekIndex = useMemo(
    () => moment(dateObject.gregorianStartDate).day(),
    [dateObject]
  )

  useEffect(() => {
    if (input.to && input.propertyCode) {
      getAllocatedList({
        variables: {
          input: input
        }
      })
    }
  }, [input.from, input.to])

  const weekendIndexes = useMemo(
    () => [
      selectedDayInDatePickerWeekIndex <= 4
        ? 4 - selectedDayInDatePickerWeekIndex
        : 11 - selectedDayInDatePickerWeekIndex,
      selectedDayInDatePickerWeekIndex <= 5
        ? 5 - selectedDayInDatePickerWeekIndex
        : 12 - selectedDayInDatePickerWeekIndex
    ],
    [dateObject]
  )
  useEffect(() => {
    setinput({
      ...input,
      from: dateObject.gregorianStartDate || '',
      to: dateObject.gregorianEndDate || ''
    })
  }, [dateObject])

  return (
    <>
      <div className={classes.root}>
        <PageHeader title={getTranslate('Allocate Availability')} />
        <Hidden smDown>
          <div className={classes.buttonContainer}>
            <ButtonBase
              component={LocalizedLink}
              to={'/allocate-availability/allocate-to-channel'}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                color="primary">
                {getTranslate('Allocate to channel')}
              </Button>
            </ButtonBase>
          </div>
          <div className={classes.selectAccommodation}>
            <AutocompleteWithLabel
              noHelperText
              value={autocompleteLabelAccommodation}
              label={getTranslate('Accommodation')}
              inputSize="small"
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              //TODO:Allocate hardcode remove filter later
              options={
                accommodationsData?.accommodations.map((item) => ({
                  label: item.basicDetails.name,
                  value: item.basicDetails.propertyCode || '',
                  code: item.basicDetails.propertyCode || '',
                  id: item.basicDetails.accommodationID || ''
                })) || []
              }
              onChange={(_, v: any) => {
                if (v) {
                  setautocompleteLabelAccommodation(v)
                  setautoCompleteLabelRoomType(null)
                  setautoCompleteLabelChannel(null)

                  setinput({
                    ...input,
                    propertyCode: v.value,
                    roomTypeCode: ''
                  })
                } else {
                  setautocompleteLabelAccommodation(null)
                  setautoCompleteLabelRoomType(null)
                  setautoCompleteLabelChannel(null)

                  setinput({
                    ...input,
                    propertyCode: '',
                    roomTypeCode: ''
                  })
                }
              }}
              loading={loadingForAccommodationData}
              loadingText={getTranslate('Loading') + '...'}
            />
          </div>
          <Collapse
            in={
              autocompleteLabelAccommodation &&
              !!autocompleteLabelAccommodation.value
            }>
            <div className={classes.filterButton}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={handleClickOnFilterButton}
                variant={!showFilters ? 'contained' : 'outlined'}
                color="primary">
                {getTranslate('Filter')}
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                className={classes.refreshButton}
                onClick={() => refetch()}
                variant={'outlined'}
                color="primary">
                {getTranslate('Refresh')}
              </Button>
            </div>
            <Collapse in={showFilters}>
              <div className={classes.filterContainer}>
                <AllocateFilterBox
                  accommodation={autocompleteLabelAccommodation}
                  roomTypeValue={autoCompleteLabelRoomType}
                  setRoomTypeValue={setautoCompleteLabelRoomType}
                  channelValue={autoCompleteLabelChannel}
                  setChannelValue={setautoCompleteLabelChannel}
                  roomTypeData={roomTypeData}
                  loadingForRoomType={loadingForRoomType}
                  getRoomTypes={getRoomTypes}
                  channelsData={channelsData?.channels}
                  loadingForChannel={loadingForChannel}
                  dateObject={dateObject}
                  getAllocatedList={getAllocatedList}
                  input={input}
                  setinput={setinput}
                />
              </div>
            </Collapse>
            <InventoryDateRow
              selectedDateInDatePicker={dateObject}
              setselectedDateInDatePicker={setDateObject}
            />
            {loadingForAllocateList ? (
              <InventorySkeleton />
            ) : (
              allocatedListData &&
              allocatedListData.allocatedList &&
              allocatedListData.allocatedList?.roomtypes.map((roomtype) => (
                <AllocateRoomtypeBox
                  roomData={roomtype}
                  weekendIndexes={weekendIndexes}
                  propertyCode={
                    (autocompleteLabelAccommodation &&
                      autocompleteLabelAccommodation.code) ||
                    ''
                  }
                />
              ))
            )}
          </Collapse>
        </Hidden>
        <Hidden mdUp>
          <AllocateMobile
            roomtypesForAllocatedData={
              (allocatedListData &&
                allocatedListData.allocatedList &&
                allocatedListData?.allocatedList.roomtypes) ||
              []
            }
            selectedAccommodation={autocompleteLabelAccommodation}
            setselectedAccommodation={setautocompleteLabelAccommodation}
            selectedChannel={autoCompleteLabelChannel}
            setselectedChannel={setautoCompleteLabelChannel}
            selectedRoomTypeCode={autoCompleteLabelRoomType}
            setselectedRoomTypeCode={setautoCompleteLabelRoomType}
            selectedDateInDatePicker={dateObject}
            setselectedDateInDatePicker={setDateObject}
            weekendIndexes={weekendIndexes}
            getAllocatedList={getAllocatedList}
          />
        </Hidden>
      </div>
    </>
  )
}

export default InventoryAllocateAvailability
